import { BulletinPatch, BulletinResource } from "@rubi/api-generated";
import dayjs from "dayjs";

import { formatPhoneNumber, stripPhoneNumberFormatting } from "@/common/utils";
import {
  ALERT_CLASSIFICATION,
  LOCATION_CLASSIFICATION,
} from "@/components/alerts/constants";
import { IPoliceService, StructuredFormatting } from "@/types";

import { isContactRowEmpty, normalizeContactRows } from "./contactRows";
import { createEmptyBulletinForm, createId } from "./defaults";
import { normalizeEntityForSave } from "./entityRules";
import {
  BulletinContact,
  BulletinEntity,
  BulletinFormValues,
  BulletinIncidentTime,
  BulletinLanguage,
  BulletinLocation,
  BulletinMediaItem,
  BulletinPdf,
  IncidentTimeType,
} from "./types";

/**
 * `BulletinFormValues` ⇄ the wire document, in both directions.
 *
 * Everything the form writes goes through here on the way out, and everything a loaded bulletin
 * carries comes back through here on the way in. The two are inverses, and `phase2.test.tsx`
 * asserts it — that round trip is what phase 8 stands on, because a field the reader drops is a
 * field an officer's edit silently deletes.
 *
 * **This module maps names and formats. It decides nothing.** What is worth saving is already
 * settled before it is called (`withSavableEntities` for entity rows), and whether a value is
 * *allowed* is phase 3's schema. Two exceptions, both of which exist because the wire is where the
 * contract is finally met rather than where a policy is chosen: the date of birth is normalised to
 * ISO through `normalizeEntityForSave` (the field stores what the officer typed — normalising per
 * keystroke would fight the cursor), and a phone number is stripped of the formatting `ContactRow`
 * displays it with.
 *
 * ## Names
 *
 * Every entity field is a plain camelCase ⇄ snake_case pair, deliberately: that half of the
 * document is new on both sides, so nothing had to be inherited. The exceptions are all at bulletin
 * level, all legacy, and all of them are columns older than this flow — renaming them would break
 * `../add/`, the public board and the OpenSearch mapping at once:
 *
 * | Form                | Wire                       |
 * | ------------------- | -------------------------- |
 * | `bulletinType`      | `type`                     |
 * | `occurrenceNumbers` | `occurrence_number`        |
 * | `associatedCrimes`  | `associated_crime`         |
 * | `policeService`     | `issuing_police_service`   |
 * | `locations`         | `location.addresses`       |
 * | `entities[].type`   | `entities[].entity_type`   |
 *
 * The last one is not legacy drift: at bulletin level `type` already means the bulletin type, so
 * the entity column could not also be called `type`, while inside the entity object there is no
 * such collision and `type` is the TypeScript idiom for a union discriminator.
 *
 * ## What the wire cannot carry
 *
 * - **Media and the PDF.** They never ride the document — decision 21. `BulletinPatch` has no slot
 *   for them, and the attachment endpoints own them.
 * - **`is_draft`.** The repository's, not the document's: saving and finalizing are the same
 *   payload with a different flag, which is the whole reason it is not in the form model.
 * - **Media itself**, still: the bytes have their own endpoints (decision 21). What *does* ride the
 *   document is `media_links`, the entity relation — see decision 46.
 * - **`policeService` beyond its id**, and **`associatedCrimes` beyond `crime`**. The wire stores an
 *   id and a crime key; the names live in option lists, which this module — being a pure mapper —
 *   has no access to. So the reader restores what is on the wire: the police service is filled in
 *   from `GET /bulletin/{id}` by `toBulletinFormFromResource` below, and a crime is **named at render
 *   time** out of `useCrimeTypeOptions`, which is why nothing here tries to.
 * - **A cleared scalar.** The endpoint reads `None` as "leave this alone", so `null` cannot mean
 *   "the officer emptied it". Text fields therefore send `""` where the backend accepts it, which
 *   does clear; `description` is the one field where `""` is sent as `null` instead, because the
 *   endpoint runs language detection and translation on whatever it is given.
 */

/* -------------------------------------------------------------------------- */
/* Wire shapes the generated client does not name                              */
/* -------------------------------------------------------------------------- */

/**
 * One entry in `location.addresses`.
 *
 * `BulletinPatch.location` is an untyped `dict` on the backend — the column predates any model of
 * it — so the shape lives here, as `mediaRepository.ts` does for `evidence_files`. The keys are the
 * ones the backend's own readers use: `geometry` for the map pins, `description` and
 * `address_components` for the OpenSearch document, and `types` / `structured_formatting` /
 * `plus_code` / `location_type` for the public-board sanitiser.
 */
export interface BulletinAddressPayload {
  description: string;
  place_id: string;
  structured_formatting?: StructuredFormatting;
  location_classification: LOCATION_CLASSIFICATION | null;
  other_location_classification: string;
  unit: string;
  /**
   * Absent until the address geocodes. Decision 4 geocodes once, at selection, and
   * `geocodePlace` answers `null` rather than throwing when Maps is unavailable — a draft whose
   * geocode failed is still worth keeping, so these ride along only when they exist and the
   * endpoint contributes no map pin for a row without them.
   */
  geometry?: google.maps.LatLngLiteral;
  address_components?: google.maps.GeocoderAddressComponent[];
  location_type?: string;
  types?: string[];
  plus_code?: google.maps.GeocoderResult["plus_code"];
}

export interface BulletinLocationPayload {
  addresses: BulletinAddressPayload[];
}

/**
 * `BulletinPatch.location` under the shape above. The generated type is an untyped dict, so the one
 * cast this module needs lives here rather than at each reader.
 */
export const locationPayload = (
  patch: BulletinPatch,
): BulletinLocationPayload | null =>
  (patch.location as BulletinLocationPayload | null | undefined) ?? null;

export type EntityPayload = NonNullable<BulletinPatch["entities"]>[number];
type ContactPayload = NonNullable<BulletinPatch["contacts"]>[number];
type IncidentTimePayload = NonNullable<BulletinPatch["incident_times"]>[number];

/* -------------------------------------------------------------------------- */
/* Form → wire                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Exported for the preview, which needs the same projection: `DisplayAlert` reads entities in the
 * wire shape, so the form's own model has to reach it through here rather than through a second
 * mapping that could disagree with what autosave sends.
 */
export const toEntityPayload = (
  entity: BulletinEntity,
  index: number,
): EntityPayload => {
  const normalized = normalizeEntityForSave(entity);
  const shared = {
    id: normalized.id,
    // Position is the order the cards are in. Nothing stores it in the form — `Person #1` is
    // derived at render time (decision 14) — so the array index is the only source there is.
    sort_ordinal: index,
    other_involvement: normalized.otherInvolvement,
    additional_description: normalized.additionalDescription,
    auto_added: normalized.autoAdded,
  };

  switch (normalized.type) {
    case "person":
      return {
        ...shared,
        entity_type: "person",
        involvement: normalized.involvement,
        first_name: normalized.firstName,
        last_name: normalized.lastName,
        date_of_birth: normalized.dateOfBirth,
      };
    case "vehicle":
      return {
        ...shared,
        entity_type: "vehicle",
        involvement: normalized.involvement,
        make: normalized.make,
        make_other: normalized.makeOther,
        model: normalized.model,
        model_other: normalized.modelOther,
        colour: normalized.colour,
        colour_other: normalized.colourOther,
        vehicle_type: normalized.vehicleType,
        vehicle_type_other: normalized.vehicleTypeOther,
        licence_plate: normalized.licencePlate,
        jurisdiction: normalized.jurisdiction,
      };
    case "item":
      return {
        ...shared,
        entity_type: "item",
        involvement: normalized.involvement,
        item_name: normalized.itemName,
      };
  }
};

const toAddressPayload = (
  location: BulletinLocation,
): BulletinAddressPayload => {
  const { input, resolved } = location;

  return {
    // The prediction's string, not the geocoder's: the two disagree often enough to matter (the
    // geocoder answers "Greater Sudbury" for an address in it) and the officer picked the
    // prediction. `BulletinLocation.input` exists for exactly this.
    description: input?.description ?? resolved?.formattedAddress ?? "",
    place_id: resolved?.placeId ?? input?.place_id ?? "",
    ...(input ? { structured_formatting: input.structured_formatting } : {}),
    location_classification: location.classification,
    other_location_classification: location.otherClassification,
    unit: location.unit,
    ...(resolved
      ? {
          geometry: resolved.geometry,
          address_components: resolved.addressComponents,
          location_type: resolved.locationType,
          types: resolved.types,
          plus_code: resolved.plusCode,
        }
      : {}),
  };
};

const toIncidentTimePayload = (
  time: BulletinIncidentTime,
): IncidentTimePayload => ({
  time_type: time.timeType,
  start_date: time.startDate?.toISOString() ?? null,
  end_date: time.endDate?.toISOString() ?? null,
});

const toContactPayload = (contact: BulletinContact): ContactPayload => ({
  name: contact.name,
  email: contact.email,
  // `ContactRow` stores the number as it is displayed — "(403) 555-0123". The column holds digits.
  phone_number: stripPhoneNumberFormatting(contact.phoneNumber),
  extension: contact.extension,
  badge_number: contact.badgeNumber,
});

/**
 * The whole document, as `PATCH /bulletin/{id}` wants it.
 *
 * Every field is always present: this is a full-document overwrite (decision 16), so a value the
 * officer removed has to be sent as empty rather than omitted.
 */
export const toBulletinPatch = (values: BulletinFormValues): BulletinPatch => ({
  type: values.bulletinType,
  occurrence_number: values.occurrenceNumbers,
  issued_time: values.issuedTime?.isValid()
    ? values.issuedTime.toISOString()
    : null,
  incident_times: values.incidentTimes.map(toIncidentTimePayload),
  entities: values.entities.map(toEntityPayload),
  // The media/entity relation travels with the entities, not with the file: it is a foreign key
  // onto them, so the row has to exist in the same transaction. Every file is listed, including the
  // ones linked to nothing — an omitted filename means "leave it alone", and clearing the last link
  // off a file has to be expressible.
  media_links: values.media.map((item) => ({
    filename: item.filename,
    entity_ids: item.entityIds,
  })),
  associated_crime: values.associatedCrimes.map((crime) => crime.crime),
  location: {
    // A row nobody has typed into is scaffolding — every section here starts with one, the same way
    // the contact list keeps a trailing blank — so it is filtered on the way out rather than
    // prevented on the way in.
    addresses: values.locations
      .filter(
        (location) => location.input !== null || location.resolved !== null,
      )
      .map(toAddressPayload),
  },
  description: values.description || null,
  description_fr: values.descriptionFr || null,
  original_language: values.originalLanguage,
  issuing_police_service: values.policeService
    ? { id: values.policeService.id }
    : null,
  contacts: values.contacts
    .filter((contact) => !isContactRowEmpty(contact))
    .map(toContactPayload),
  operational_area: values.operationalArea,
  approved_by: values.approvedBy,
  approved_by_badge: values.approvedByBadge,
  classification: values.classification,
});

/* -------------------------------------------------------------------------- */
/* Wire → form                                                                 */
/* -------------------------------------------------------------------------- */

const toEntityValues = (payload: EntityPayload): BulletinEntity => {
  const shared = {
    id: payload.id,
    autoAdded: payload.auto_added ?? false,
    additionalDescription: payload.additional_description ?? "",
    otherInvolvement: payload.other_involvement ?? "",
  };

  switch (payload.entity_type) {
    case "person":
      return {
        ...shared,
        type: "person",
        involvement: payload.involvement ?? null,
        firstName: payload.first_name ?? "",
        lastName: payload.last_name ?? "",
        dateOfBirth: payload.date_of_birth ?? null,
      };
    case "vehicle":
      return {
        ...shared,
        type: "vehicle",
        involvement: payload.involvement ?? null,
        make: payload.make ?? "",
        makeOther: payload.make_other ?? "",
        model: payload.model ?? "",
        modelOther: payload.model_other ?? "",
        colour: payload.colour ?? "",
        colourOther: payload.colour_other ?? "",
        vehicleType: payload.vehicle_type ?? "",
        vehicleTypeOther: payload.vehicle_type_other ?? "",
        licencePlate: payload.licence_plate ?? "",
        jurisdiction: payload.jurisdiction ?? "",
      };
    case "item":
      return {
        ...shared,
        type: "item",
        involvement: payload.involvement ?? null,
        itemName: payload.item_name ?? "",
      };
  }
};

const toLocationValues = (
  address: BulletinAddressPayload,
): BulletinLocation => ({
  // Row ids are client-side (they key the React rows, nothing else), so a loaded row gets a fresh
  // one rather than borrowing something off the wire.
  id: createId(),
  // What the autocomplete shows. It reads `description` and `structured_formatting` and nothing
  // else — the two classification fields are on `PlaceType` because the legacy `AddressList` kept
  // the location type inside the prediction, and `LocationRow` reads the row's own instead.
  input: {
    description: address.description,
    place_id: address.place_id,
    structured_formatting: address.structured_formatting ?? {
      main_text: address.description,
      secondary_text: "",
    },
    location_classification:
      address.location_classification ?? LOCATION_CLASSIFICATION.OTHER,
  },
  resolved: address.geometry
    ? {
        placeId: address.place_id,
        formattedAddress: address.description,
        addressComponents: address.address_components ?? [],
        geometry: address.geometry,
        locationType: address.location_type ?? "",
        types: address.types ?? [],
        plusCode: address.plus_code,
      }
    : null,
  classification: address.location_classification,
  // Defaulted for the same reason as `unit` on the next line: the stored dict is reached
  // through a cast, so a missing key is `undefined` in a field declared as a string.
  otherClassification: address.other_location_classification ?? "",
  unit: address.unit ?? "",
});

const toIncidentTimeValues = (
  payload: IncidentTimePayload,
): BulletinIncidentTime => ({
  id: createId(),
  timeType: payload.time_type as IncidentTimeType,
  startDate: payload.start_date ? dayjs(payload.start_date) : null,
  endDate: payload.end_date ? dayjs(payload.end_date) : null,
});

/**
 * Every field is defaulted, because `BulletinContact` declares them all as required strings and
 * the wire cannot promise that. `contacts` is a free-form JSON column on the bulletin, and the
 * reader above reaches it through a cast — so a row stored without a key arrives as `undefined`
 * in a field the rest of the flow calls `.trim()` on. `formatPhoneNumber` is the loudest of
 * those: it starts with `.replace()`, so it throws rather than returning something wrong.
 */
const toContactValues = (payload: ContactPayload): BulletinContact => ({
  id: createId(),
  name: payload.name ?? "",
  email: payload.email ?? "",
  phoneNumber: formatPhoneNumber(payload.phone_number ?? ""),
  extension: payload.extension ?? "",
  badgeNumber: payload.badge_number ?? "",
});

/**
 * A stored bulletin, as the form reads it.
 *
 * Written onto `createEmptyBulletinForm()` rather than assembled field by field, so a field the
 * wire has nothing to say about keeps its empty-form value instead of arriving as `undefined` and
 * making every reader in the flow handle a third state.
 *
 * The empty form's own scaffolding rows are what a missing list falls back to: a bulletin with no
 * incident time still shows one row to fill in.
 *
 * Phase 8 reads `GET /bulletin/{id}`, whose `BulletinResource` already satisfies this shape — bar
 * `contacts`, which FastAPI types as a bare `list` and the generated client therefore as
 * `unknown[]`, the same gap `mediaRepository.ts` crosses for `evidence_files`.
 */
export const toBulletinForm = (patch: BulletinPatch): BulletinFormValues => {
  const empty = createEmptyBulletinForm();
  const addresses = locationPayload(patch)?.addresses;

  return {
    ...empty,

    bulletinType: patch.type ?? null,
    occurrenceNumbers: patch.occurrence_number ?? [],
    operationalArea: patch.operational_area ?? null,

    // Neither `group` nor `name` is on the wire, and a pure mapper has no list to look them up in.
    // The key is what the pickers and the preview match on, and `useCrimeTypeOptions.getOptionLabel`
    // is what turns it back into a name on screen.
    associatedCrimes: (patch.associated_crime ?? []).map((crime) => ({
      crime,
      group: "",
    })),
    incidentTimes: patch.incident_times?.length
      ? patch.incident_times.map(toIncidentTimeValues)
      : empty.incidentTimes,
    locations: addresses?.length
      ? addresses.map(toLocationValues)
      : empty.locations,

    entities: (patch.entities ?? []).map(toEntityValues),

    description: patch.description ?? "",
    descriptionFr: patch.description_fr ?? "",
    originalLanguage: (patch.original_language as BulletinLanguage) ?? "en",

    // The trailing blank is the contact list's own scaffolding, so it is added back here rather
    // than by the section on first render.
    contacts: normalizeContactRows((patch.contacts ?? []).map(toContactValues)),
    approvedBy: patch.approved_by ?? "",
    approvedByBadge: patch.approved_by_badge ?? "",
    classification:
      (patch.classification as ALERT_CLASSIFICATION) ?? empty.classification,
    issuedTime: patch.issued_time ? dayjs(patch.issued_time) : null,
  };
};

/**
 * A bulletin as `GET /bulletin/{id}` returns it, as the form reads it.
 *
 * `toBulletinForm` above does almost all of it, because `BulletinResource` is `BulletinPatch` plus
 * the things the wire cannot express — and those extra three are exactly what its "What the wire
 * cannot carry" list said phase 8 would have to fill in:
 *
 * - **The police service as an object**, not an id. The form field takes the object, not an id, so
 *   without this the form would open holding nothing at all in it.
 * - **Media and the PDF**, which never ride the document (decision 21) and come back on the
 *   resource instead — `entityIds` included, since decision 43.
 * - **`is_draft`**, which the caller reads rather than the form: it belongs to the repository.
 *
 * Nothing is lost across the round trip any more. **Which entity each file is attached to** was the
 * one gap — the link lived on the media item and only in the browser — and it closed on 2026-08-19
 * with `evidence_files[*].entity_ids`: decision 43, and `phase8.test.tsx` asserts the restore where
 * it used to assert the loss.
 */
export const toBulletinFormFromResource = (
  resource: BulletinResource,
  // Required, not defaulted: `toMediaSnapshot` is the one reader of `evidence_files` and it lives
  // in `persistence/` with the endpoints that also return it, so this cannot read them itself
  // without the model depending on persistence. A default would turn "forgot to pass them" into a
  // bulletin that silently opens with no media.
  media: BulletinMediaItem[],
  pdf: BulletinPdf | null,
): BulletinFormValues => ({
  // The cast is the gap the doc comment above `toBulletinForm` names: FastAPI types `contacts` as a
  // bare `list`, so the generated client says `unknown[]`. Every other field lines up.
  ...toBulletinForm(resource as unknown as BulletinPatch),

  // Same cast `usePoliceServiceOptions` makes, and for the same reason: the generated
  // `GroupResource` is wider than portal's hand-written group types, and `IPoliceService` is a
  // `Pick` of three of its fields.
  policeService:
    (resource.issuing_police_service as unknown as IPoliceService | null) ??
    null,

  media,
  pdf,
});
